import type { Command } from 'common/Prose/Commands'
import { NodeSelection, Selection } from 'prosemirror-state'
import { liftTarget } from 'prosemirror-transform'
import { deleteBarrier, findCutBefore, textblockAt } from './utils'

// NOTICE: Plucked from node_modules/prosemirror-commands/src/commands.js
// :: (EditorState, ?(tr: Transaction), ?EditorView) → bool
// If the selection is empty and at the start of a textblock, try to
// reduce the distance between that block and the one before it—if
// there's a block directly before it that can be joined, join them.
// If not, try to move the selected block closer to the next one in
// the document structure by lifting it out of its parent or moving it
// into a parent of the previous block. Will use the view for accurate
// (bidi-aware) start-of-textblock detection if given.
export const joinBackward: Command = (state, dispatch, view) => {
  let $cursor: Selection<any>['$anchor'] = (state.selection as any).$cursor // FIXME: Fix this type
  if (!$cursor || (view ? !view.endOfTextblock('backward', state) : $cursor.parentOffset > 0)) {
    return false
  }

  let $cut = findCutBefore($cursor)

  // If there is no node before this, try to lift
  if (!$cut) {
    let range = $cursor.blockRange(),
      target = range && liftTarget(range)
    if (target == null) {
      return false
    }
    if (dispatch && range) {
      dispatch(state.tr.lift(range, target).scrollIntoView())
    }
    return true
  }

  let before = $cut.nodeBefore
  // Apply the joining algorithm
  if (!before?.type.spec.isolating && deleteBarrier(state, $cut, dispatch)) {
    return true
  }

  // If the node below has no content and the node above is
  // selectable, delete the node below and select the one above.
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  if ($cursor.parent.content.size === 0 && (textblockAt(before, 'end') || NodeSelection.isSelectable(before!))) {
    if (dispatch) {
      let tr = state.tr.deleteRange($cursor.before(), $cursor.after())
      const selection = textblockAt(before, 'end')
        ? Selection.findFrom(tr.doc.resolve(tr.mapping.map($cut.pos, -1)), -1)
        : // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          NodeSelection.create(tr.doc, $cut.pos - before!.nodeSize)
      if (selection) {
        tr.setSelection(selection)
      }
      dispatch(tr.scrollIntoView())
    }
    return true
  }

  // If the node before is an atom, delete it
  if (before?.isAtom && $cut.depth === $cursor.depth - 1) {
    if (dispatch) {
      dispatch(state.tr.delete($cut.pos - before.nodeSize, $cut.pos).scrollIntoView())
    }
    return true
  }

  return false
}
